import { useApolloClient } from "@apollo/client";
import { Text } from "@hackthenorth/north";
import { Button, Modal, TextInput } from "north.js";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import {
  allocateBadgeId,
  badgeAppModeForRole,
} from "src/views/volunteer/registration/badge-registration/badgeData";
import { BadgeProvisioner } from "src/views/volunteer/registration/badge-registration/badgeProvisioner";
import { badgeRestorePlan } from "src/views/volunteer/registration/badge-registration/badgeRestore";
import { useRegisterBadgeCodeToEventAttendeeMutation } from "src/views/volunteer/registration/graphql/registerBadgeCodeToEventAttendee.generated";
import { useRegisterBadgeUploadTokenMutation } from "src/views/volunteer/registration/graphql/registerBadgeUploadToken.generated";

import { loadAdminSetupTarget } from "./adminSetup";

export default function AdminBadgeTools({
  device,
  connected,
  busy,
  setBusy,
  invalidateUpload,
  onDisconnect,
}: {
  device: BadgeProvisioner | null;
  connected: boolean;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  invalidateUpload: () => void;
  onDisconnect: () => void;
}) {
  const client = useApolloClient();
  const [registerBadge] = useRegisterBadgeCodeToEventAttendeeMutation();
  const [registerToken] = useRegisterBadgeUploadTokenMutation();
  const [setupMode, setSetupMode] = useState<"fresh" | "restore">("fresh");
  const [email, setEmail] = useState("");
  const [target, setTarget] =
    useState<
      ReturnType<typeof loadAdminSetupTarget> extends Promise<infer T>
        ? T
        : never
    >();
  const [inspection, setInspection] =
    useState<
      ReturnType<BadgeProvisioner["inspectForSetup"]> extends Promise<infer T>
        ? T
        : never
    >();
  const restorePlan = useMemo(() => {
    if (!target?.backup) return null;
    try {
      return badgeRestorePlan(target.backup.value);
    } catch {
      return null;
    }
  }, [target]);
  const canRestore = Boolean(
    restorePlan &&
      (restorePlan.files.length ||
        restorePlan.launcherOrder.length ||
        restorePlan.pals?.length)
  );
  const [resetOpen, setResetOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(false);
  useEffect(() => {
    setInspection(undefined);
    setResetOpen(false);
  }, [connected]);

  return (
    <Tools>
      <Text mods="h3 heading">Register or replace a badge</Text>
      <Text>
        Look up a hacker without connecting a badge. Review their profile, then
        connect a factory-clean badge and choose a fresh start or restore their
        saved progress. Setup writes their registration and a new sync
        credential.
      </Text>
      <Text mods="grey">
        This uses your organizer registration access. The station password above
        is required when saving a badge snapshot.
      </Text>
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (busy || !email.trim()) return;
          setBusy(true);
          setError(false);
          setTarget(undefined);
          setInspection(undefined);
          setSetupMode("fresh");
          setStatus("Looking up this attendee and their saved backup…");
          try {
            setTarget(await loadAdminSetupTarget(client, email));
            if (device && connected)
              setInspection(await device.inspectForSetup());
            setStatus("Review the attendee and badge below before setup.");
          } catch (failure) {
            setError(true);
            setStatus(
              failure instanceof Error && !("graphQLErrors" in failure)
                ? failure.message
                : "Could not look up the attendee. Check the email and your organizer session."
            );
          } finally {
            setBusy(false);
            if (device && !device.connected && connected) onDisconnect();
          }
        }}
      >
        <label htmlFor="badge-setup-email">
          <Text mods="bold textBody">Hacker email</Text>
          <TextInput
            id="badge-setup-email"
            type="email"
            size="md"
            autoComplete="off"
            required
            value={email}
            disabled={busy}
            onChange={(event) => {
              setEmail(event.target.value);
              setTarget(undefined);
            }}
          />
        </label>
        <Button
          type="submit"
          color="secondary"
          disabled={busy || !email.trim()}
        >
          Look up hacker
        </Button>
      </form>
      {connected && !inspection && (
        <Button
          color="secondary"
          disabled={busy}
          onClick={async () => {
            if (!device || busy) return;
            setBusy(true);
            setError(false);
            try {
              setInspection(await device.inspectForSetup());
              setStatus("Badge inspected. Review setup options below.");
            } catch {
              setError(true);
              setStatus(
                "Could not inspect this badge. Reconnect and try again."
              );
            } finally {
              setBusy(false);
              if (!device.connected) onDisconnect();
            }
          }}
        >
          Inspect connected badge
        </Button>
      )}
      {!connected && (
        <Text>
          Connect a badge when you are ready to program it. Lookup does not
          change the hacker’s data.
        </Text>
      )}
      {inspection && (
        <Text>
          Connected badge: {inspection.mac} ·{" "}
          {inspection.factoryClean
            ? "Factory clean"
            : "Not factory clean — reset it before setup"}
          {inspection.identity?.display_name
            ? ` · ${inspection.identity.display_name}`
            : ""}
        </Text>
      )}
      {target && (
        <>
          <Text mods="h4 heading">
            {target.identity.display_name} · {target.email}
          </Text>
          <Text>
            {target.identity.role} · Attendee #{target.eventAttendeeId} · Claim
            #{target.claimId}
          </Text>
          <Text>
            Badge ID:{" "}
            {target.assignedBadgeId ||
              "A new ID will be allocated during setup"}
          </Text>
          <dl>
            {[
              ["Name", target.identity.display_name],
              ["Email shared on badge", target.identity.net_email],
              ["Phone", target.identity.net_phone],
              ["LinkedIn", target.identity.net_linkedin],
              ["Discord", target.identity.net_discord],
              ["Instagram", target.identity.net_instagram],
              ["X / Twitter", target.identity.net_x],
            ].map(([label, value]) => (
              <React.Fragment key={label}>
                <dt>{label}</dt>
                <dd>{value || "Not shared"}</dd>
              </React.Fragment>
            ))}
          </dl>
          <fieldset>
            <legend>What should go on this badge?</legend>
            <label title="Writes the hacker's latest registration and a new credential. Starts game progress and contacts fresh. Their server backup and Goose Games points are preserved.">
              <input
                type="radio"
                name="badge-setup-mode"
                checked={setupMode === "fresh"}
                disabled={busy}
                onChange={() => setSetupMode("fresh")}
              />
              Register a fresh badge
            </label>
            <label title="Restores complete saved game records, contacts, sponsor/workshop visits, public app unlocks and pals. Keeps the destination wallet key. Unsynced data and installed Lua apps cannot be restored.">
              <input
                type="radio"
                name="badge-setup-mode"
                checked={setupMode === "restore"}
                disabled={busy || !canRestore}
                onChange={() => setSetupMode("restore")}
              />
              Register and import saved progress
            </label>
            <Text>
              {target.backup
                ? `Saved backup: ${new Date(
                    target.backup.updatedAt
                  ).toLocaleString()}`
                : "No saved backup is available for this hacker."}
            </Text>
            {target.backup && !canRestore && (
              <Text>
                This backup has no supported progress to import. You can still
                register a fresh badge.
              </Text>
            )}
            {restorePlan?.partial && (
              <Text>
                This backup is partial. Import restores only complete saved
                records; missing progress cannot be recovered from it.
              </Text>
            )}
            <Text>
              {setupMode === "restore"
                ? "Imports saved games, contacts, visits, app unlocks and pals. Unsynced progress and installed Lua apps are not included. The badge keeps its wallet key."
                : "Starts with fresh game progress and contacts. The hacker’s server backup and Goose Games points are kept."}
            </Text>
          </fieldset>
          {target.assignedBadgeId && (
            <Text>
              This replaces the sync credential for the attendee’s existing
              badge ID. Their previous badge will no longer be able to upload.
              Their saved backup is preserved.
            </Text>
          )}
          <span
            title={
              !connected
                ? "Connect a badge, then inspect it."
                : !inspection?.factoryClean
                ? "Inspect the badge and factory reset it if it has an existing registration or data."
                : "Writes only the reviewed hacker's profile; replaces their previous badge's sync credential."
            }
          >
            <Button
              color="primary"
              disabled={!connected || busy || !inspection?.factoryClean}
              onClick={async () => {
                if (!device || !connected || busy || !inspection?.factoryClean)
                  return;
                setBusy(true);
                setError(false);
                invalidateUpload();
                setStatus(
                  "Checking the latest profile and programming the badge… Keep it connected."
                );
                let programmingStarted = false;
                let registrationSaved = false;
                try {
                  const current = await loadAdminSetupTarget(
                    client,
                    target.email
                  );
                  if (JSON.stringify(current) !== JSON.stringify(target)) {
                    setTarget(current);
                    setStatus(
                      "This attendee’s profile or registration changed. Review the updated preview, then press Set up badge again."
                    );
                    return;
                  }
                  const checked = await device.inspectForSetup();
                  setInspection(checked);
                  if (!checked.factoryClean || checked.mac !== inspection.mac)
                    throw new Error(
                      "The connected badge changed or is not factory clean. Look up the attendee again after checking the badge."
                    );
                  const badgeId =
                    current.assignedBadgeId ||
                    (await allocateBadgeId({
                      virtual: false,
                      register: async (badgeCode) => {
                        const saved = await registerBadge({
                          variables: {
                            event_attendee_id: current.eventAttendeeId,
                            user_id: current.userId,
                            badge_code: badgeCode,
                          },
                        });
                        if (
                          saved.errors?.length ||
                          saved.data?.registerBadgeCodeToEventAttendee
                            .badge_code !== badgeCode
                        )
                          throw new Error();
                      },
                    }));
                  if (setupMode === "restore") {
                    if (!current.backup) throw new Error("No backup");
                    badgeRestorePlan(current.backup.value); // validate before programming
                  }
                  programmingStarted = true;
                  const result = await device.program(
                    {
                      ...current.identity,
                      badge_id: badgeId,
                      provisioned_unix: Math.floor(Date.now() / 1000),
                    },
                    badgeAppModeForRole(current.identity.role),
                    { requireClean: true }
                  );
                  const rawToken = Array.from(
                    window.crypto.getRandomValues(new Uint8Array(32))
                  )
                    .map((byte) => byte.toString(16).padStart(2, "0"))
                    .join("");
                  await device.writeUploadToken(rawToken);
                  const saved = await registerToken({
                    variables: {
                      event_attendee_id: current.eventAttendeeId,
                      claim_id: current.claimId,
                      badge_code: badgeId,
                      badge_mac: result.mac,
                      badge_secure_token_hash:
                        "sha256:" +
                        Array.from(
                          new Uint8Array(
                            await window.crypto.subtle.digest(
                              "SHA-256",
                              new TextEncoder().encode(rawToken)
                            )
                          )
                        )
                          .map((byte) => byte.toString(16).padStart(2, "0"))
                          .join(""),
                    },
                  });
                  if (
                    !saved.data?.registerBadgeUploadToken ||
                    saved.errors?.length
                  )
                    throw new Error();
                  registrationSaved = true;
                  if (setupMode === "restore" && current.backup) {
                    setStatus(
                      "Registration saved. Importing saved progress… Keep the badge connected."
                    );
                    await device.restoreBackup(
                      current.backup.value,
                      current.claimId
                    );
                    onDisconnect();
                  }
                  setInspection({
                    provisioned: true,
                    factoryClean: false,
                    mac: result.mac,
                    identity: result.identity,
                    appMode: result.appMode,
                  });
                  setTarget(undefined);
                  setEmail("");
                  setStatus(
                    setupMode === "restore"
                      ? "Badge setup complete. Saved progress was imported and the badge restarted. Reconnect it to sync."
                      : "Badge setup complete. Fresh registration, sync credential and badge MAC are saved. It is ready to sync."
                  );
                } catch {
                  setError(true);
                  setInspection(undefined);
                  setStatus(
                    registrationSaved
                      ? "Registration is saved, but importing progress did not finish. Keep the badge at the help desk. Factory reset it, then choose Import saved progress to retry; the server backup is still available."
                      : programmingStarted
                      ? "Badge setup did not finish. Keep it at the desk: factory reset and repeat setup, or recover it from Registration before handing it out."
                      : "Setup could not start. Check eligibility and reconnect the badge, then look up the attendee again."
                  );
                } finally {
                  setBusy(false);
                  if (device && !device.connected && connected) onDisconnect();
                }
              }}
            >
              Set up badge
            </Button>
          </span>
        </>
      )}
      <Text mods="h3 heading">Badge tools</Text>
      <Text>
        Unlock all public apps for this badge, including sponsor badges. This
        leaves its registration, saved data and diagnostic access unchanged.
      </Text>
      <Actions>
        <span title="Makes the connected badge a Ripple origin at depth 0, replacing its previous Ripple depth. It spreads through successful badge connections; registration and game scores are unchanged.">
          <Button
            color="secondary"
            disabled={!connected || busy}
            onClick={async () => {
              if (!device || !connected || busy) return;
              setBusy(true);
              setError(false);
              invalidateUpload();
              setStatus("Seeding virology on the connected badge…");
              try {
                await device.seedVirology();
                setStatus(
                  "Virology seeded: this badge is a Ripple origin (depth 0). Open Connect on both badges to spread it through badge connections. Sync to save this state to the hacker’s backup."
                );
              } catch {
                setError(true);
                setStatus(
                  "Virology seeding was not confirmed. Reconnect with the current firmware and try again."
                );
              } finally {
                setBusy(false);
                if (!device.connected) onDisconnect();
              }
            }}
          >
            Seed virology (Ripple)
          </Button>
        </span>
        <Button
          color="secondary"
          disabled={!connected || busy}
          onClick={async () => {
            if (!device || busy) return;
            setBusy(true);
            setError(false);
            invalidateUpload();
            try {
              const added = await device.unlockPublicApps();
              setStatus(
                `All public apps are unlocked. ${added} ${
                  added === 1 ? "app was" : "apps were"
                } added.`
              );
            } catch {
              setError(true);
              setStatus(
                "Public app unlock was not confirmed. Reconnect and try again."
              );
            } finally {
              setBusy(false);
              if (device && !device.connected && connected) onDisconnect();
            }
          }}
        >
          Unlock all public apps
        </Button>
        <Button
          color="secondary"
          disabled={!connected || busy}
          onClick={async () => {
            if (!device || busy) return;
            setBusy(true);
            setError(false);
            try {
              setInspection({
                ...(await device.inspect()),
                factoryClean: false,
              });
              setResetOpen(true);
            } catch {
              setError(true);
              setStatus(
                "Could not inspect this badge. Reconnect before resetting it."
              );
            } finally {
              setBusy(false);
              if (device && !device.connected && connected) onDisconnect();
            }
          }}
        >
          Factory reset badge
        </Button>
      </Actions>
      {status && (
        <Text role={error ? "alert" : "status"} aria-live="polite">
          {status}
        </Text>
      )}
      <Modal
        open={resetOpen}
        onClose={() => {
          if (!busy) setResetOpen(false);
        }}
      >
        <Modal.Header>Factory reset this badge?</Modal.Header>
        <Modal.Body>
          <div
            data-private="true"
            data-hj-suppress="true"
            className="ph-no-capture"
          >
            <Text>
              {inspection?.identity?.display_name || "Unassigned badge"} ·{" "}
              {inspection?.mac}
            </Text>
            <Text>
              This removes registration, the on-badge sync credential, contacts,
              settings, app progress and installed Lua apps. Unsynced data will
              be lost. The badge will reboot.
            </Text>
            <Text>
              The device’s Solana wallet key and the server’s saved backup are
              preserved. This reset does not erase the wallet for a different
              owner.
            </Text>
          </div>
        </Modal.Body>
        <Modal.Footer justify="end">
          <Button
            color="secondary"
            disabled={busy}
            onClick={() => setResetOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={busy || !connected}
            onClick={async () => {
              if (!device || busy || !inspection) return;
              setBusy(true);
              setError(false);
              invalidateUpload();
              try {
                const current = await device.inspect();
                if (
                  current.mac !== inspection.mac ||
                  JSON.stringify(current.identity) !==
                    JSON.stringify(inspection.identity)
                )
                  throw new Error();
                await device.factoryReset();
                setTarget(undefined);
                setInspection(undefined);
                setStatus(
                  "Factory reset complete. Reconnect the badge before setup. Its wallet key was preserved."
                );
                onDisconnect();
              } catch {
                setError(true);
                setStatus(
                  "Factory reset was not confirmed and may be incomplete. Reconnect, inspect the badge and retry the reset before setup."
                );
              } finally {
                setResetOpen(false);
                setBusy(false);
                if (device && !device.connected && connected) onDisconnect();
              }
            }}
          >
            Reset badge
          </Button>
        </Modal.Footer>
      </Modal>
    </Tools>
  );
}

const Tools = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
  form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  fieldset {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }
  input[type="radio"] {
    margin-right: 8px;
  }
  dl {
    margin: 0;
  }
  dt {
    font-weight: 600;
  }
  dd {
    margin: 4px 0 12px;
    overflow-wrap: anywhere;
  }
`;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;
